import * as React from 'react';
import { isEqual } from 'lodash';

import CampaignBudgetPieChart from '../campaign-budget-pie-chart/CampaignBudgetPieChart';
import CampaignPieWidget from '../campaign-pie-widget/CampaignPieWidget';
import CampaignRoiTable from '../campaign-roi-table/CampaignRoiTable';
import CostAttributionMissing from '../cost-attribution-missing/CostAttributionMissing';

import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';

applyTranslations(require('./campaign-budget-widget.i18n.json'));

import './campaign-budget-widget.scss';

type Props = {
  campaignId: number,
  isOutdated: boolean,
  hasAgreedOffers: boolean,
  totalBudget: number,
  isLoading: boolean,
  budget: {
    [x: string]: number
  },
  roi: {
    [x: string]: number
  }
};

class CampaignBudgetWidget extends React.Component<Props> {
  renderContent(hasOnlyOneMetric: boolean): React.ReactNode {
    const { budget, campaignId, hasAgreedOffers, isOutdated, totalBudget } = this.props;

    if (isOutdated) {
      return (
        <div className="campaign-budget-widget__empty">
          <CostAttributionMissing
            campaignId={campaignId}
            hasAgreedOffers={hasAgreedOffers}
          />
        </div>
      );
    }

    if (hasOnlyOneMetric) {
      return (
        <div className="campaign-budget-widget__total">
          {renderPrice(totalBudget)}
        </div>
      );
    }

    return (
      <div className="campaign-budget-widget__graph">
        <CampaignBudgetPieChart
          budget={budget}
          totalBudget={totalBudget}
        />
      </div>
    );
  }

  renderTable = () => {
    const { budget, isOutdated } = this.props;
    const availableDeliverableTypes = Object.keys(budget || {}).filter(key => budget[key] > 0);

    if (isOutdated) {
      return null;
    }

    return (
      <CampaignRoiTable
        roi={this.props.roi}
        deliverableTypesToShow={availableDeliverableTypes}
      />
    );
  };

  renderBudgetDisclaimer = () => (
    <div className="campaign-budget-widget__title-info">
      {__t('campaign_budget_widget.budget_disclaimer')}
      <a
        color="light"
        rel="noopener noreferrer"
        target="_blank"
        href={`/campaigns/${this.props.campaignId}/report/v2/cca`}
        className="campaign-budget-widget__link"
      >
        {__t('campaign_budget_widget.cca_link')}
      </a>
    </div>
  );

  render() {
    const { budget, totalBudget, isOutdated, isLoading } = this.props;
    const hasOnlyOneMetric = Object.keys(budget || {}).filter(type => budget[type]).length === 1;

    return (
      <CampaignPieWidget
        title={__t('campaign_budget_widget.title')}
        total={renderPrice(totalBudget)}
        disclaimer={!isOutdated && this.renderBudgetDisclaimer()}
        table={this.renderTable()}
        content={this.renderContent(hasOnlyOneMetric)}
        hideTotal={hasOnlyOneMetric && !isOutdated}
        hasOnlyOneMetric={hasOnlyOneMetric}
        isLoading={isLoading}
      />
    );
  }
}

export default React.memo(
  CampaignBudgetWidget,
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
);
